import '../../styles/LandingPage/ResearchPillars/MortalityMorbidity.css';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function MortalityMorbidity() {
  const contentRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const items = contentRef.current?.querySelectorAll('.mm-animate');
    items?.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section className="mm-detail-section" ref={contentRef}>
      <div className="mm-detail-container">
        <Link to="/" className="mm-back-link mm-animate">
          <i className="ti ti-arrow-left" />
          Back to Research Pillars
        </Link>

        <div className="mm-detail-header mm-animate">
          <div className="mm-detail-label">Pillar 03</div>
          <div className="mm-detail-icon">
            <i className="ti ti-activity" />
          </div>
          <h1>Mortality & Morbidity Studies</h1>
          <p className="mm-detail-subtitle">
            Experience analysis, persistency modelling, and mortality improvement assumptions grounded in African demographic realities.
          </p>
        </div>

        <div className="mm-content">
          <div className="mm-block mm-animate">
            <h2>Experience Analysis</h2>
            <p>
              Reliable mortality and morbidity assumptions are the foundation of life insurance pricing and reserving. 
              Our research conducts granular experience studies using data from African insurers, examining how actual 
              experience deviates from standard tables such as SA85-90, GA1951, and country-specific tables. 
              We investigate the impact of HIV/AIDS on mortality patterns, the emergence of non-communicable diseases, 
              and the heterogeneity of mortality experience across socioeconomic segments and geographic regions.
            </p>
            <div className="mm-highlight">
              <i className="ti ti-bulb" />
              <span>Key Insight: Mortality experience in urban vs. rural populations can diverge by 30-50%, yet most African tables do not stratify by geography.</span>
            </div>
          </div>

          <div className="mm-block mm-animate">
            <h2>Lapse and Persistency Behaviour</h2>
            <p>
              Policy persistency is a critical driver of profitability for long-term life products. Our research models 
              lapse behaviour as a function of economic conditions, product features, and policyholder characteristics. 
              We examine the "emergency fund" effect — where policyholders surrender policies during economic stress — 
              and develop dynamic lapse models that respond to macroeconomic indicators. Understanding anti-selective 
              lapses, where healthier lives leave risk pools, is essential for maintaining portfolio viability.
            </p>
          </div>

          <div className="mm-block mm-animate">
            <h2>Mortality Improvement Assumptions</h2>
            <p>
              Setting appropriate mortality improvement assumptions requires balancing historical trends with future projections. 
              Our research adapts international improvement scales — such as the CMI model and SSA scales — to African contexts, 
              accounting for the different stages of epidemiological transition across the continent. We examine how 
              improvements in healthcare access, nutrition, and sanitation interact with emerging health challenges 
              to shape long-term mortality trajectories.
            </p>
          </div>

          <div className="mm-block mm-animate">
            <h2>Data Credibility & Small Portfolio Issues</h2>
            <p>
              Many African insurers operate with limited exposure data, making credibility-weighted estimation essential. 
              Our research develops Bayesian and Bühlmann credibility frameworks tailored to small portfolio contexts, 
              showing how to optimally blend company experience with industry or population data. We address the 
              challenges of data quality, including incomplete records, inconsistent coding, and the impact of 
              data cleaning on statistical conclusions.
            </p>
          </div>

          <div className="mm-block mm-animate">
            <h2>Current Research Outputs</h2>
            <div className="mm-outputs">
              <div className="mm-output-card">
                <i className="ti ti-file-analytics" />
                <h4>African Mortality Experience Study 2024</h4>
                <span>Industry Report · 2025</span>
              </div>
              <div className="mm-output-card">
                <i className="ti ti-chart-line" />
                <h4>Dynamic Lapse Model for Emerging Markets</h4>
                <span>Technical Paper · 2025</span>
              </div>
              <div className="mm-output-card">
                <i className="ti ti-database" />
                <h4>Credibility-Weighted Assumption Setter</h4>
                <span>Open-source Tool · 2025</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}